import { Box, Button, Flex, Grid, Popup, SortOrder, Spacing, Table, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtom, useAtomValue } from 'jotai';
import { useChangeNetSuiteRequest, useCurrencyPrecision } from 'pages/requestForm/containers/netsuite/hooks';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';
import { GetNetSuiteBillPaymentBills, useGetNetSuiteBillPaymentBills } from 'shared/data/webApp/v2';
import { netSuiteBillPaymentApplyListState } from 'shared/states';

import { useNetSuiteBillPaymentBillsFilters } from '../../../../NetSuiteBillPaymentRequestForm.hooks';
import {
    netSuiteBillPaymentBillsFilterTransferState,
    netSuiteBillPaymentSelectableBillsState,
} from '../../../../NetSuiteBillPaymentRequestForm.states';
import { getColumns } from './AddBillsPopup.config';
import { useTableRowData } from './AddBillsPopup.hooks';
import { messages } from './AddBillsPopup.messages';
import { AddBillsPopupProps, SortingConfig } from './AddBillsPopup.types';
import { BillsFilter } from './components/BillsFilter/BillsFilter';
import { EmptyBillsSection } from './components/EmptyBillsSection/EmptyBillsSection';

export const AddBillsPopup = memo((props: AddBillsPopupProps) => {
    const { companyId, currency, accountId, billPaymentRequestId, payeeId, payeeType, togglePopup } = props;

    const [sortingConfig, setSortingConfig] = useState<SortingConfig>();
    const [billsPage, setBillsPage] = useState(1);
    const keepPreviousBills = useRef(false);

    const applyList = useAtomValue(netSuiteBillPaymentApplyListState);
    const selectableBills = useAtomValue(netSuiteBillPaymentSelectableBillsState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const [filterTransfer, setFilterTransfer] = useAtom(netSuiteBillPaymentBillsFilterTransferState);

    const currencyPrecision = useCurrencyPrecision(currency);

    const { checkedBillIds, totalAmountOfSelectedBills, addBills, updateSelectableBills, onCheckedBillsChange } =
        useNetSuiteBillPaymentBillsFilters(currencyPrecision);

    const { data, isLoading, isFetching } = useGetNetSuiteBillPaymentBills({
        query: {
            payeeId,
            accountId,
            payeeType,
            currencyId: currency.id,
            pageSize: 200,
            page: billsPage,
            ...filterTransfer,
        },
        path: { companyId, billPaymentRequestId },
    });

    const isAddBillsDisabled = useMemo(
        () =>
            checkedBillIds.length === applyList.length &&
            checkedBillIds.every((checkedBillId) => applyList.some(({ id }) => id === checkedBillId)),
        [applyList, checkedBillIds]
    );

    const hasMoreBills = useMemo(() => data?.items.length && data.items.length % 50 === 0, [data]);

    const rowData = useTableRowData(companyId, selectableBills, currencyPrecision);

    const columns = useMemo(() => getColumns(sortingConfig), [sortingConfig]);

    useEffect(() => {
        if (data?.items) {
            updateSelectableBills(data.items, keepPreviousBills.current);
            keepPreviousBills.current = false;
        }
    }, [data, updateSelectableBills]);

    useUnmount(() => {
        updateSelectableBills([]);
    });

    const handleAddBills = useCallback(() => {
        addBills();
        togglePopup();

        changeNetSuiteRequest();
    }, [changeNetSuiteRequest, addBills, togglePopup]);

    const onSort = useCallback(
        (columnId: string, newSortOrder: SortOrder) => {
            setSortingConfig({
                sortBy: columnId,
                sortOrder: newSortOrder,
            });

            setFilterTransfer((prev) => ({
                ...prev,
                sortColumn: columnId,
                sortOrder: newSortOrder,
            }));

            setBillsPage(1);
        },
        [setFilterTransfer]
    );

    const setFilters = useCallback(
        (filters: Partial<GetNetSuiteBillPaymentBills['params']['query']>) => {
            setFilterTransfer(filters);
            setBillsPage(1);
        },
        [setFilterTransfer]
    );

    const handleBillsTableEndReached = useCallback(() => {
        if (hasMoreBills) {
            setBillsPage((currentBillsPage) => currentBillsPage + 1);
            keepPreviousBills.current = true;
        }
    }, [hasMoreBills]);

    return (
        <>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button size='medium' color='blue80' onClick={handleAddBills} disabled={isAddBillsDisabled}>
                        {messages.addButtonText}
                    </Button>
                }
                progress={isFetching}
            />

            <Popup.Body spacing='32'>
                <Grid>
                    <BillsFilter currency={currency} isLoading={isFetching} setFilters={setFilters} />

                    <Flex alignItems='center' justifyContent='end' spacing='4'>
                        <Text font='label'>{messages.amountLabel}</Text>

                        <Box spacing='0 0 0 20' textAlign='right'>
                            <Text font='label' fontWeight='bold'>
                                {intl.formatCurrency(totalAmountOfSelectedBills, currency.text)}
                            </Text>
                        </Box>
                    </Flex>

                    <Spacing height={20} />

                    {rowData.length > 0 && (
                        <Box overflowX='auto' height={400}>
                            <Table
                                columns={columns}
                                data={rowData}
                                checkedItems={checkedBillIds}
                                onCheckedItemsChange={onCheckedBillsChange}
                                handleEndReached={handleBillsTableEndReached}
                                onSort={onSort}
                                stickyHeader
                                virtualized
                            />
                        </Box>
                    )}

                    <EmptyBillsSection selectableBills={selectableBills} isLoading={isLoading} />
                </Grid>
            </Popup.Body>
        </>
    );
});

AddBillsPopup.displayName = 'AddBillsPopup';
